import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const INITIAL_STATE = {
  invoice_number: '', amount: '', credit_type: 'cash',
  remarks: '', party_name: '', reference_number: '',
  linked_sale_invoice: '',
};

export const CreditEntryForm = ({ selectedDate, onEntryAdded }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [pendingSalesList, setPendingSalesList] = useState([]);

  useEffect(() => {
    const fetchPendingSales = async () => {
      try {
        const response = await axios.get(`${API}/sales/pending-list`);
        setPendingSalesList(response.data);
      } catch (err) {
        toast.error('Error loading pending sales');
      }
    };
    fetchPendingSales();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.invoice_number || !form.amount) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/credits`, {
        date: selectedDate,
        invoice_number: form.invoice_number,
        amount: parseFloat(form.amount),
        credit_type: form.credit_type,
        remarks: form.remarks,
        party_name: form.party_name,
        reference_number: form.reference_number,
        linked_sale_invoice: form.linked_sale_invoice,
      });
      toast.success('Credit added!');
      setForm(INITIAL_STATE);
      onEntryAdded();
    } catch (err) {
      toast.error('Failed to add credit');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleLinkSale = (billNumber) => {
    const selected = pendingSalesList.find(s => s.bill_number === billNumber);
    if (selected) {
      setForm(prev => ({
        ...prev,
        linked_sale_invoice: billNumber,
        invoice_number: billNumber,
        amount: selected.amount.toString(),
        party_name: selected.customer_name,
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="credit-entry-form">
      {pendingSalesList.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
          <Label className="text-sm font-semibold text-blue-900">Link to Pending Sale (Optional)</Label>
          <Select value={form.linked_sale_invoice} onValueChange={handleLinkSale}>
            <SelectTrigger className="bg-white mt-2"><SelectValue placeholder="Select from pending sales..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">-- None --</SelectItem>
              {pendingSalesList.map(sale => (
                <SelectItem key={sale.bill_number} value={sale.bill_number}>
                  {sale.bill_number} - {sale.customer_name} - ₹{sale.amount}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-sm font-semibold">Invoice Number *</Label>
        <Input value={form.invoice_number} onChange={(e) => update('invoice_number', e.target.value)} placeholder="INV-001" required />
      </div>
      <div>
        <Label className="text-sm font-semibold">Amount (₹) *</Label>
        <Input type="number" step="0.01" value={form.amount} onChange={(e) => update('amount', e.target.value)} className="currency" placeholder="0.00" required />
      </div>
      <div>
        <Label className="text-sm font-semibold">Credit Type *</Label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" value="cash" checked={form.credit_type === 'cash'} onChange={(e) => update('credit_type', e.target.value)} className="w-4 h-4" />
            <span className="text-sm">Cash</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" value="bank" checked={form.credit_type === 'bank'} onChange={(e) => update('credit_type', e.target.value)} className="w-4 h-4" />
            <span className="text-sm">Bank</span>
          </label>
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold">Party Name</Label>
        <Input value={form.party_name} onChange={(e) => update('party_name', e.target.value)} placeholder="Party/Source" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Reference Number</Label>
        <Input value={form.reference_number} onChange={(e) => update('reference_number', e.target.value)} placeholder="Ref #" />
      </div>
      <div>
        <Label className="text-sm font-semibold">Remarks</Label>
        <Input value={form.remarks} onChange={(e) => update('remarks', e.target.value)} placeholder="Notes" />
      </div>
      <Button type="submit" disabled={loading} className="w-full premium-button bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700" data-testid="submit-credit-btn">
        {loading ? 'Adding...' : 'Add Credit'}
      </Button>
    </form>
  );
};
